/**
 * EN-1557: the backend's live push for the Download Sessions card.
 *
 * The AgmHub sends ReceiveDownloadCommandsChanged to the device's own group
 * when anyone queues, cancels, edits the range of,
 * retries or deletes a queued download, or when it is sent to the device.
 * The message only says which device changed; the web reloads the cards
 * from GET /download-sessions/recent (same group scope as the list), so the
 * collapsed card's counts update even when the Download Manager is closed.
 * The list-driven reload (queuedDownloadCommandsLoaded) stays as the
 * fallback, for example while the hub connection is down.
 */

/** SignalR event sent by the AgmHub when a device's queued downloads changed. */
export const DOWNLOAD_COMMANDS_CHANGED_EVENT = 'ReceiveDownloadCommandsChanged';

/** Vuex action dispatched for that event (debounced reload of the cards). */
export const DOWNLOAD_COMMANDS_CHANGED_ACTION =
  'downloadCommandsChangedReceived';

/** Payload of ReceiveDownloadCommandsChanged (DownloadCommandsChangedMsg). */
export interface DownloadCommandsChangedMsg {
  uid: number;
  timestampUtc?: string;
}

interface HubConnectionLike {
  on(eventName: string, handler: (data: any) => void): void;
}

/** True when a user is signed in (has a token). */
export function isSignedIn(user: unknown): boolean {
  const token = (user as { token?: unknown } | null | undefined)?.token;
  return typeof token === 'string' && token.length > 0;
}

/**
 * Registers the ReceiveDownloadCommandsChanged handler on the AgmHub
 * connection. Pushes for devices outside the user's device list are ignored
 * (the hub already sends to the device's group only; this is a second check,
 * as for the other device events).
 */
export function registerDownloadCommandsChangedHandler(
  connection: HubConnectionLike,
  isUidInGroup: (uid: number) => boolean,
  dispatch: (type: string, payload: DownloadCommandsChangedMsg) => void
): void {
  connection.on(
    DOWNLOAD_COMMANDS_CHANGED_EVENT,
    (data: DownloadCommandsChangedMsg | null | undefined) => {
      if (data == null || typeof data.uid !== 'number') return;
      if (!isUidInGroup(data.uid)) return;
      dispatch(DOWNLOAD_COMMANDS_CHANGED_ACTION, data);
    }
  );
}
